import { ReportsAdapter } from '~/components/Reports/services/adapters/reports.adapter';
import {
    IReportMonthlyData,
    IReportYearlyData,
    ILeadsReportsParams,
    IReportDataByYear
} from '~/components/Reports/types/reports.types';
import {
    FIRST_DAY_OF_MONTH_INDEX,
    FIRST_MONTH_OF_YEAR_INDEX,
    formatDate,
    MONTHS_PER_YEAR,
    TDate
} from '~/utils/date.utils';
import { sortByValue } from '~/utils/sort.utils';
import { ReportFiltersType } from '~/components/Reports/types/reports.types';
export const REPORTS_SERVICE_DATE_FORMAT = 'MM D, YYYY';

export class ReportsService extends ReportsAdapter {
    async getTransformedReportAnalytics(
        userId: string,
        filters?: ReportFiltersType
    ): Promise<IReportDataByYear> {
        const reportAnalytics = await super.getReportAnalytics(userId, filters);
        const { period_entries: periodEntries = [] } = reportAnalytics;

        return Promise.resolve(this.getTransformedReportData(periodEntries));
    }

    async getTransformedReportSales(
        userId: string,
        filter?: ReportFiltersType
    ): Promise<IReportDataByYear> {
        const reportSales = await super.getReportSales(userId, filter);
        const { period_entries: periodEntries = [] } = reportSales;

        return Promise.resolve(this.getTransformedReportData(periodEntries));
    }

    getTransformedReportData(
        periodEntries: IReportMonthlyData[]
    ): IReportDataByYear {
        return this.getReportDataGroupedByYears(
            this.getReportDataPaddedWithMissingMonths(
                this.getReportDataWithDateStringsAsTimestamp(
                    this.getReportDataSortedAscending(periodEntries)
                )
            )
        );
    }
    getEmptyYearGroup(): IReportYearlyData {
        return {
            files_sent_sum: 0,
            files_booked_sum: 0,
            files_booked_out_of_sent: 0,
            files_booked_payments_value_sum: 0,
            tax_collected_sum: 0,
            proposals_sent_sum: 0,
            proposals_booked_sum: 0,
            proposals_booked_out_of_sent: 0,
            invoices_sent_sum: 0,
            invoices_booked_sum: 0,
            contracts_sent_sum: 0,
            contracts_booked_sum: 0,
            period_entries: []
        };
    }

    protected getReportDataSortedAscending(
        periodEntries: Array<IReportMonthlyData>
    ): Array<IReportMonthlyData> {
        return periodEntries.sort((periodEntryA, periodEntryB) => {
            return periodEntryA.timestamp > periodEntryB.timestamp ? 1 : -1;
        });
    }

    protected getReportDataWithDateStringsAsTimestamp(
        periodEntries: Array<IReportMonthlyData>
    ): Array<IReportMonthlyData> {
        return periodEntries.map(periodEntry => ({
            ...periodEntry,
            timestamp: this.formatDate(periodEntry.timestamp)
        }));
    }

    protected formatDate(date: TDate) {
        return formatDate(date, REPORTS_SERVICE_DATE_FORMAT);
    }

    protected getReportDataPaddedWithMissingMonths(
        periodEntries: Array<IReportMonthlyData>
    ): Array<IReportMonthlyData> {
        const currentYear = new Date().getFullYear();
        const firstTimestamp = periodEntries?.[0]?.timestamp;
        const firstReportYear = firstTimestamp
            ? new Date(firstTimestamp).getFullYear()
            : currentYear;

        const numReportYears = currentYear - firstReportYear + 1;

        return this.getPaddedPeriodEntries(
            periodEntries,
            firstReportYear,
            numReportYears
        );
    }

    protected getPaddedPeriodEntries(
        periodEntries: Array<IReportMonthlyData>,
        firstReportYear: number,
        numReportYears: number
    ): Array<IReportMonthlyData> {
        const periodEntriesTsMap = this.getPeriodEntriesTsMap(periodEntries);
        const paddedPeriodEntries = [];
        let currentPeriodEntryTs = this.formatDate(
            new Date(
                firstReportYear,
                FIRST_MONTH_OF_YEAR_INDEX,
                FIRST_DAY_OF_MONTH_INDEX
            )
        );

        for (let i = 0; i < numReportYears * MONTHS_PER_YEAR; i++) {
            paddedPeriodEntries.push(
                periodEntriesTsMap?.[currentPeriodEntryTs] || {
                    timestamp: currentPeriodEntryTs,
                    files_booked_out_of_sent: '0%'
                }
            );

            currentPeriodEntryTs =
                this.getNextPeriodEntryTs(currentPeriodEntryTs);
        }

        return paddedPeriodEntries;
    }

    protected getPeriodEntriesTsMap(periodEntries: Array<IReportMonthlyData>): {
        [key: string]: IReportMonthlyData;
    } {
        const periodEntriesTsMap: { [key: string]: IReportMonthlyData } = {};
        periodEntries.forEach(periodEntry => {
            periodEntriesTsMap[periodEntry.timestamp] = periodEntry;
        });

        return periodEntriesTsMap;
    }

    protected getNextPeriodEntryTs(currentPeriodEntryTs: string): string {
        const nextPeriodEntryDate = new Date(currentPeriodEntryTs);
        nextPeriodEntryDate.setMonth(nextPeriodEntryDate.getMonth() + 1);

        return this.formatDate(nextPeriodEntryDate);
    }

    protected getReportDataGroupedByYears(
        periodEntries: Array<IReportMonthlyData>
    ): IReportDataByYear {
        const yearGroups: IReportDataByYear = {};
        let currentYear = null;

        periodEntries.forEach(periodEntry => {
            let yearGroup;
            const periodEntryYear = new Date(
                periodEntry.timestamp
            ).getFullYear();
            if (periodEntryYear !== currentYear) {
                currentYear = periodEntryYear;
                yearGroup = this.getEmptyYearGroup();
                yearGroups[periodEntryYear] = yearGroup;
            } else {
                yearGroup = yearGroups[periodEntryYear];
            }

            yearGroup.files_sent_sum += periodEntry.files_sent || 0;
            yearGroup.files_booked_sum += periodEntry.files_booked || 0;
            yearGroup.files_booked_payments_value_sum +=
                periodEntry.files_booked_payments_value || 0;
            yearGroup.tax_collected_sum += periodEntry.tax_collected || 0;
            yearGroup.files_booked_out_of_sent =
                yearGroup.files_sent_sum === 0
                    ? 0
                    : (yearGroup.files_booked_sum / yearGroup.files_sent_sum) *
                      100;
            yearGroup.proposals_sent_sum += periodEntry.proposals_sent || 0;
            yearGroup.proposals_booked_sum += periodEntry.proposals_booked || 0;
            yearGroup.proposals_booked_out_of_sent =
                yearGroup.proposals_sent_sum === 0
                    ? 0
                    : (yearGroup.proposals_booked_sum /
                          yearGroup.proposals_sent_sum) *
                      100;
            yearGroup.invoices_sent_sum += periodEntry.invoices_sent || 0;
            yearGroup.invoices_booked_sum += periodEntry.invoices_booked || 0;
            yearGroup.contracts_sent_sum += periodEntry.contracts_sent || 0;
            yearGroup.contracts_booked_sum += periodEntry.contracts_booked || 0;
            yearGroup.period_entries.push(periodEntry);
        });

        return yearGroups;
    }

    async getLeadsForCharts(userId: string, user_data: ILeadsReportsParams) {
        const leads = await super.getLeadsReportsData(userId, user_data);
        const { report } = leads;

        let graphsData = {
            count: [],
            avg_lead_value: [],
            conversion_rate: [],
            avg_time_to_booked: []
        };

        for (let leadKey in report) {
            if (leadKey && report.hasOwnProperty(leadKey)) {
                let metricData = report[leadKey];
                for (let metricKey in metricData) {
                    if (metricData.hasOwnProperty(metricKey)) {
                        graphsData[metricKey] = graphsData[metricKey] || [];
                        let currValue = metricData[metricKey];
                        if (metricKey === 'avg_time_to_booked') {
                            // convert seconds to days
                            currValue = Math.round(currValue / 86400);
                        } else if (metricKey === 'conversion_rate') {
                            currValue = Math.round(currValue * 100);
                        }

                        graphsData[metricKey].push({
                            name: leadKey,
                            value: currValue || undefined
                        });
                    }
                }
            }
        }

        const distribution = graphsData.count.sort(sortByValue).reverse();

        const leadsValue = graphsData.avg_lead_value
            .sort(sortByValue)
            .reverse();

        const conversionRate = graphsData.conversion_rate
            .sort(sortByValue)
            .reverse();

        const timeToBook = graphsData.avg_time_to_booked.sort(sortByValue);

        return { distribution, leadsValue, conversionRate, timeToBook };
    }
}
